import { useRef, useMemo, useContext, useEffect, useState } from 'react';

import './TagsCell.styles.scss';
import Button from '../../Button/Button';
import { iTag } from '../../../core/models/Tag';
import { GlobalContext } from '../../../GlobalContext';

interface TagsCellProps {
    rowId: number;
    tags: iTag[];
    isExpanded: boolean;
    toggleRowExpansion: (id: number) => void;
    onTagClick?: (tag: iTag) => void;
}

const TAG_GAP = 4;
const FONT_AND_PADDING = 12;
const MORE_BUTTON_WIDTH = 66;
const AVG_CHAR_WIDTH_RATIO = 0.6;

const TagsCell = ({ rowId, tags, isExpanded, toggleRowExpansion, onTagClick }: TagsCellProps) => {
    const { getRem } = useContext(GlobalContext);

    const fontAndPadding = getRem(FONT_AND_PADDING);
    const tagsGap = getRem(TAG_GAP);

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(300);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const updateWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                if (width > 0 && width !== containerWidth) {
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => {
                        setContainerWidth(width);
                    }, 16);
                }
            }
        };

        updateWidth();

        const resizeObserver = new ResizeObserver(updateWidth);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, [containerWidth]);

    const tagWidths = useMemo(() => {
        return tags.map((t) => predictTagWidth(t, fontAndPadding));
    }, [tags, fontAndPadding]);

    const { visibleCount, needsMore } = useMemo(() => {
        const prediction = predictVisibleTagsWithWidths(tagWidths, containerWidth);

        if (isExpanded) {
            return { visibleCount: tagWidths.length, needsMore: prediction.needsMore };
        }

        return prediction;
    }, [tagWidths, containerWidth, isExpanded]);

    const visibleTags = isExpanded ? tags : tags.slice(0, visibleCount);
    const showMoreButton = !isExpanded && needsMore && visibleCount < tags.length;
    const showLessButton = isExpanded && needsMore;

    return (
        <div ref={containerRef} className="TagsCell" style={{ gap: tagsGap }}>
            {visibleTags.map((t) => (
                <Button
                    key={t.id}
                    className="TagsCell__tag"
                    style={{ fontSize: fontAndPadding, padding: `${tagsGap} ${fontAndPadding}` }}
                    unstyled
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onTagClick?.(t);
                    }}
                >
                    {t.title}
                </Button>
            ))}
            {(showMoreButton || showLessButton) && (
                <Button
                    className="TagsCell__expandable-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleRowExpansion(rowId);
                    }}
                    unstyled
                >
                    {showLessButton ? '...less' : '...more'}
                </Button>
            )}
        </div>
    );
};

/**
 * Predicts the rendered width of a tag element based on its text content.
 * Uses the AVG_CHAR_WIDTH_RATIO as a heuristic to estimate the width without expensive DOM measurements.
 */
function predictTagWidth(tag: iTag, fontAndPaddingRem: number): number {
    const textWidth = tag.title.length * fontAndPaddingRem * AVG_CHAR_WIDTH_RATIO;
    return textWidth + fontAndPaddingRem * 2;
}

/**
 * Calculates how many tags fit in the available width
 * Accounts for gaps between tags
 */
function calculateTagsInRow(tagWidths: number[], availableWidth: number, startIndex: number = 0): number {
    let currentWidth = 0;
    let count = 0;

    for (let i = startIndex; i < tagWidths.length; i++) {
        const tagWidth = tagWidths[i];
        const widthWithGap = count === 0 ? tagWidth : tagWidth + TAG_GAP;

        if (currentWidth + widthWithGap <= availableWidth) {
            currentWidth += widthWithGap;
            count++;
        } else {
            break;
        }
    }

    return count;
}

/**
 * Predicts how many tags should be visible before showing "more" button
 * Uses container width and pre-calculated tag widths for better performance
 */
function predictVisibleTagsWithWidths(tagWidths: number[], containerWidth: number): { visibleCount: number; needsMore: boolean } {
    if (tagWidths.length === 0 || containerWidth === 0) {
        return { visibleCount: 0, needsMore: false };
    }

    const firstRowCount = calculateTagsInRow(tagWidths, containerWidth);

    if (firstRowCount >= tagWidths.length) {
        return { visibleCount: tagWidths.length, needsMore: false };
    }

    const remainingTags = tagWidths.length - firstRowCount;
    const remainingTagWidths = tagWidths.slice(firstRowCount);
    const secondRowMaxCount = calculateTagsInRow(remainingTagWidths, containerWidth);

    if (secondRowMaxCount >= remainingTags) {
        return { visibleCount: tagWidths.length, needsMore: false };
    }

    const secondRowWithMoreWidth = containerWidth - MORE_BUTTON_WIDTH - TAG_GAP;
    const secondRowWithMoreCount = calculateTagsInRow(remainingTagWidths, secondRowWithMoreWidth);

    const totalVisibleCount = firstRowCount + secondRowWithMoreCount;

    return {
        visibleCount: totalVisibleCount,
        needsMore: true
    };
}

export default TagsCell;
